import React from "react";
import { ChevronDown } from "lucide-react";
import { useT } from "@/lib/LanguageContext";

/**
 * INLINE NAV CAPACITY — how many links a header may draw in one row, and where the rest go.
 *
 * BUG-045 (Re-Open). The shared nav model hands a complete site up to TEN links (5
 * `ANCHOR_SECTIONS` + 5 `INTERIOR_PAGES`, lib/siteLinks.js) and each header draws them beside
 * a wordmark, a language switch, an account link and a "Book a call" button — none of which
 * are responsive. Classic solved this once (components/site/Navbar.jsx: a fixed inline count
 * per breakpoint band plus a "More" disclosure) but the five THEMED chromes each kept
 * `links.map(...)` with no bound at all, so at laptop widths the row simply outgrew its
 * container: measured on a practice named "Émilie Houde Naturopathe" in French, Warm/Organic
 * overflowed its bar by 303px at 1536px, Clinical by 163px, Luxe by 205px, Bold by 21px, and
 * Minimal's pill wrapped to two lines — with the Book button pushed off the right edge of the
 * SCREEN on four of the five. See scripts/browser_qa/bug045_header_nav_overflow.py.
 *
 * WHY A FIXED COUNT RATHER THAN "HOPE IT FITS PAST SOME BREAKPOINT". The overhead is not
 * responsive and the brand name is arbitrary-length, so there is no width at which ten French
 * labels reliably fit: Organic and Clinical still overflowed at 1536px. A count that is
 * bounded BY DESIGN is the only thing that cannot be defeated by a long label — and a practice
 * with fewer links than the count in play never renders the disclosure at all, so the common
 * case shows every link inline with nothing hidden.
 *
 * WHY THE COUNTS DIFFER PER THEME. They are not a style choice; they are a width budget, and
 * each theme spends its width differently (Bold sets 11px uppercase with `gap-5`, Organic 14px
 * semibold with `gap-6`, Luxe 10px uppercase split across two halves either side of the
 * wordmark). Each theme's constants are declared in its own chrome file next to the layout
 * that justifies them, with the measured overflow they were chosen against.
 *
 * WHY `matchMedia` AND NOT A SECOND, CSS-ONLY COPY OF THE NAV. With one JS-computed split a
 * link is either inline or in the overflow list, never both, so there is exactly one element
 * (and one `data-testid`) per link at any moment — no duplicate half-hidden nodes for
 * assistive tech to trip over. `matchMedia` does not exist under jsdom; the hook then keeps
 * its narrow-band default, which is the band the component tests exercise.
 */

/** Tailwind's `lg` and `xl`. The bands every chrome switches its desktop nav on at. */
export const LG_PX = 1024;
export const XL_PX = 1280;

/**
 * Split the nav model into what fits inline and what goes into the "More" menu.
 *
 * WHICH LINKS FOLD FIRST IS NOT ARBITRARY. The sheet's own words are "secondary navigation
 * items should be grouped", and the two halves of this model are not equally important:
 *
 *   * `pages` (/reserver, /boutique, /programmes, /journal, /learn) are DESTINATIONS that
 *     exist nowhere else in the header. BUG-006 was precisely that no themed header linked to
 *     an interior page at all; folding them first would re-create that bug at every laptop
 *     width, which is what the first cut of this fix did.
 *   * `sections` (#story, #approach, #services, #testimonials, #contact) are same-page jumps.
 *     A visitor reaches them by scrolling the homepage, and all six footers list every one of
 *     them. They are the secondary set, so they are the set that folds.
 *
 * Reading order is preserved in BOTH lists — the row still reads anchors-then-pages and the
 * menu still reads in model order. Only the choice of what to fold is prioritised.
 *
 * @param {{sections: Array, pages: Array}} model  the nav model's two groups
 * @param {object} capacity  `{ lg, xl }` — how many links may be inline in each band. A chrome
 *                           whose desktop nav only appears at `xl` still passes both; the
 *                           `lg` value is then simply never used on screen.
 * @param {number} [breakpoint] the width at which `xl` takes over (default Tailwind's xl)
 * @returns {{inline: Array, overflow: Array, sectionsInline: Array, pagesInline: Array}}
 *          `sectionsInline`/`pagesInline` are for the one theme (Luxe) that draws the two
 *          groups in two separate halves either side of its wordmark.
 */
export function useInlineNavSplit(model, capacity, breakpoint = XL_PX) {
  const query = `(min-width: ${breakpoint}px)`;
  const [isWide, setIsWide] = React.useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false,
  );
  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(query);
    const onChange = (e) => setIsWide(e.matches);
    setIsWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  const count = isWide ? capacity.xl : capacity.lg;
  const sections = model.sections || [];
  const pages = model.pages || [];
  // Pages first: they are the destinations. Sections take whatever capacity is left over,
  // and the ones that do not fit are the ones the disclosure holds.
  const keptPages = Math.min(pages.length, Math.max(count, 0));
  const keptSections = Math.max(0, count - keptPages);
  const sectionsInline = sections.slice(0, keptSections);
  const pagesInline = pages.slice(0, keptPages);
  return {
    sectionsInline,
    pagesInline,
    inline: [...sectionsInline, ...pagesInline],
    overflow: [...sections.slice(keptSections), ...pages.slice(keptPages)],
  };
}

/**
 * The "More" disclosure — one `shrink-0` flex child holding every link that did not fit.
 *
 * Renders NOTHING when `items` is empty, so a practice whose whole nav fits never sees it.
 * Styling is passed in, exactly like the shared `LangSwitch`: the disclosure is one
 * behaviour with five appearances, not five implementations.
 *
 * Behaviour matches Classic's (components/site/Navbar.jsx): `aria-expanded` + `aria-controls`,
 * closes on outside click, closes on Escape, closes when a link inside it is followed. The
 * links keep their `data-testid`, so "every destination is reachable" stays assertable.
 */
export const NavMore = ({
  items = [],
  className = "",
  menuClassName = "",
  itemClassName = "",
  // Two themes paint their panels with `var(--tpl-*)` through an inline style rather than a
  // Tailwind class (Organic's warm secondary, Luxe's translucent navy), so the menu takes an
  // inline style the same way their own panels do.
  menuStyle = undefined,
  align = "center",
  iconSize = 14,
}) => {
  const tt = useT("templates");
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onOutsideClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onOutsideClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onOutsideClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!items.length) return null;

  const position = align === "right"
    ? "right-0"
    : align === "left"
      ? "left-0"
      : "left-1/2 -translate-x-1/2";

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        data-testid="nav-more-toggle"
        aria-expanded={open}
        aria-controls="nav-more-menu"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 whitespace-nowrap ${className}`}
      >
        {tt("shared.nav.more")}
        <ChevronDown size={iconSize} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          id="nav-more-menu"
          data-testid="nav-more-menu"
          className={`absolute top-full mt-3 min-w-[200px] z-50 py-2 ${position} ${menuClassName}`}
          style={menuStyle}
        >
          {items.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-link-${l.key}`}
              onClick={() => setOpen(false)}
              className={`block whitespace-nowrap px-4 py-2 ${itemClassName}`}
            >
              {l.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default NavMore;
